import type {
    AiAgentMeetingPayload,
    CompanyInfo,
    Competitor,
    FullThread,
    UserInfo,
    Contact,
} from "app/pages/sales-intelligence/modules/ai-agent/types/meeting";
import type { FeedbackDto, Thread } from "app/pages/sales-intelligence/modules/ai-agent/types/thread";
import type {
    ForceResultWithFullResponse,
    IFetchService,
    PostConfigObject } from "app/services/fetchService";
import type {
    GetRequestOptions
} from "app/services/fetchService";

const DEFAULT_URL = "/sales-api/ai-meeting-agent";
const THREADS_URL = `${DEFAULT_URL}/threads`;

export default function createMeetingAgentApiService(apiClient: IFetchService) {
    return {
        getCompanyInfo: (params: { domain: string }, options?: Readonly<GetRequestOptions>): Promise<CompanyInfo> => {
            return apiClient.get(`${DEFAULT_URL}/company-info`, params, options);
        },
        getCompetitors: (
            params: { domain: string },
            options?: Readonly<GetRequestOptions>,
        ): Promise<{ competitors: Competitor[] }> => {
            return apiClient.get(`${DEFAULT_URL}/competitors`, params, options);
        },
        getUserInfo: (options?: Readonly<GetRequestOptions>): Promise<UserInfo> => {
            return apiClient.get(`${DEFAULT_URL}/user-info`, undefined, options);
        },
        createAIAgentThread: (
            payload: AiAgentMeetingPayload,
            options?: Readonly<PostConfigObject>,
        ): Promise<ForceResultWithFullResponse<string>> => {
            return apiClient.post(`${THREADS_URL}`, payload, options);
        },
        postAIAgentMessage: (
            {
                threadId,
                message,
            }: {
                threadId: string;
                message: string;
            },
            options?: Readonly<PostConfigObject>,
        ): Promise<ForceResultWithFullResponse<string>> => {
            return apiClient.post(`${THREADS_URL}/${threadId}/message`, { message }, options);
        },
        getThreads: (options?: Readonly<GetRequestOptions>): Promise<Thread[]> => {
            return apiClient.get(`${THREADS_URL}`, undefined, options);
        },
        getThreadById: (threadId: string, options?: Readonly<GetRequestOptions>): Promise<FullThread> => {
            return apiClient.get(`${THREADS_URL}/${threadId}`, undefined, options);
        },
        putAIAgentMessageFeedback: (
            { threadId, messageId, liked }: FeedbackDto,
            options?: Readonly<GetRequestOptions>,
        ): Promise<void> => {
            return apiClient.put(
                `${THREADS_URL}/${threadId}/messages/${messageId}/feedback?liked=${liked}`,
                undefined,
                options,
            );
        },
        getContacts: (
            params: { domain: string; search?: string; limit?: number },
            options?: Readonly<GetRequestOptions>,
        ): Promise<Contact[]> => {
            return apiClient.get(`${DEFAULT_URL}/contacts/search`, params, options);
        },
    };
}
